/**
 * Centralized risk-ordering logic for report sections:
 * - Top Strategic Risks
 * - Recommendations & Notes
 * - Remediation Roadmap
 *
 * Single source of truth to prevent drift between sections.
 */

import { lookupResidualRisk } from "@/lib/riskMatrix";

/** Explicit priority order for sorting (lower rank = higher priority) */
export const PRIORITY_ORDER = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
} as const;

/** Backward-compatible alias */
export const PRIORITY_WEIGHT_RANK: Record<string, number> = { ...PRIORITY_ORDER };

/** Normalize priority_weight to a supported value */
export function normalizePriorityWeight(weight: string | null | undefined): keyof typeof PRIORITY_ORDER {
  const normalized = (weight ?? "medium").toLowerCase().trim();
  if (normalized === "critical" || normalized === "high" || normalized === "medium" || normalized === "low") {
    return normalized;
  }
  return "medium";
}

/** Normalize incoming score to a finite number */
export function normalizeScore(score: number | string | null | undefined, fallback = 50): number {
  const parsed = Number(score);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Resolve a numeric rank for a priority_weight string. Defaults to medium (2). */
export function priorityRank(weight: string | null | undefined): number {
  return PRIORITY_ORDER[normalizePriorityWeight(weight)];
}

/**
 * Central residual-risk function (display priority):
 * - priority_weight = inherent risk
 * - maturity = control effectiveness
 * - final priority = residual risk
 *
 * Delegates to the authoritative matrix in riskMatrix.ts.
 */
export function calculatePriority(
  questionWeight: string | null | undefined,
  maturityLabel: string | null | undefined,
  _traceKey?: string,
): keyof typeof PRIORITY_ORDER {
  const weight = normalizePriorityWeight(questionWeight);

  if (_traceKey) {
    console.log(`[TRACE calculatePriority] key=${_traceKey} | raw_weight="${questionWeight}" -> normalized="${weight}" | raw_maturity="${maturityLabel}"`);
  }

  // Delegate to authoritative matrix
  return lookupResidualRisk(weight, maturityLabel);
}

/** Backward-compatible alias */
export function adjustedPriority(
  questionWeight: string | null | undefined,
  maturityLabel: string | null | undefined,
): keyof typeof PRIORITY_ORDER {
  return calculatePriority(questionWeight, maturityLabel);
}

/**
 * Standard comparator for risk-based sorting.
 * 1. priority_weight (critical > high > medium > low)
 * 2. score ascending (lowest/worst first)
 * 3. questionIndex ascending (tie-breaker only)
 */
export function riskComparator(
  a: { priorityWeight: string | null | undefined; score: number | string | null | undefined; questionIndex: number | null | undefined },
  b: { priorityWeight: string | null | undefined; score: number | string | null | undefined; questionIndex: number | null | undefined },
): number {
  const pDiff = priorityRank(a.priorityWeight) - priorityRank(b.priorityWeight);
  if (pDiff !== 0) return pDiff;

  const sDiff = normalizeScore(a.score) - normalizeScore(b.score);
  if (sDiff !== 0) return sDiff;

  return (a.questionIndex ?? 0) - (b.questionIndex ?? 0);
}

/** Group labels for recommendation sections */
export type RecommendationGroup =
  | "Critical Actions"
  | "High Priority Actions"
  | "Medium Priority Improvements"
  | "Maintain & Optimize";

/** All groups in display order */
export const RECOMMENDATION_GROUPS: RecommendationGroup[] = [
  "Critical Actions",
  "High Priority Actions",
  "Medium Priority Improvements",
  "Maintain & Optimize",
];

/**
 * Assign a group label based on residual priority_weight.
 * - critical → Critical Actions
 * - high → High Priority Actions
 * - medium → Medium Priority Improvements
 * - low → Maintain & Optimize
 */
export function assignGroup(
  priorityWeight: string | null | undefined,
  _maturityLabel?: string | null,
): RecommendationGroup {
  const pw = normalizePriorityWeight(priorityWeight);
  if (pw === "critical") return "Critical Actions";
  if (pw === "high") return "High Priority Actions";
  if (pw === "medium") return "Medium Priority Improvements";
  return "Maintain & Optimize";
}

/** Priority badge label derived from priority weight */
export function priorityLabelFromWeight(priorityWeight: string | null | undefined): "Critical" | "High" | "Medium" | "Low" {
  const pw = normalizePriorityWeight(priorityWeight);
  if (pw === "critical") return "Critical";
  if (pw === "high") return "High";
  if (pw === "medium") return "Medium";
  return "Low";
}

/** Score-based priority label for badges (kept for legacy consumers) */
export function priorityFromScore(score: number): "Critical" | "High" | "Medium" | "Low" {
  const normalizedScore = normalizeScore(score);
  if (normalizedScore <= 10) return "Critical";
  if (normalizedScore <= 25) return "High";
  if (normalizedScore <= 50) return "Medium";
  return "Low";
}

/** Priority badge color classes */
export function priorityColor(priority: "Critical" | "High" | "Medium" | "Low"): string {
  if (priority === "Critical") return "bg-red-200 text-red-900";
  if (priority === "High") return "bg-red-100 text-red-800";
  if (priority === "Medium") return "bg-amber-100 text-amber-800";
  return "bg-green-100 text-green-800";
}

/** Group header color classes (text + border) */
export const GROUP_COLORS: Record<RecommendationGroup, { text: string; border: string; icon: string }> = {
  "Critical Actions":              { text: "text-red-900 dark:text-red-300",   border: "border-red-500 dark:border-red-600",   icon: "text-red-700" },
  "High Priority Actions":         { text: "text-red-700 dark:text-red-400",   border: "border-red-300 dark:border-red-700",   icon: "text-red-600" },
  "Medium Priority Improvements":  { text: "text-amber-700 dark:text-amber-400", border: "border-amber-300 dark:border-amber-700", icon: "text-amber-600" },
  "Maintain & Optimize":           { text: "text-green-700 dark:text-green-400", border: "border-green-300 dark:border-green-700", icon: "text-green-600" },
};
